import { useState, useMemo } from "react";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { Product } from "@/sdk/types.ts";
import ProductCardSmall from "./product-card-small";
import { TrackWrapper } from "./track/wrapper";

const assignRandomCategory = (product: Product) => {
  const categories = ["Clothing", "Shoes", "Accessories", "Bags"];
  return {
    ...product,
    category: categories[Math.floor(Math.random() * categories.length)],
  };
};

export default function CartBlock({
  similarRecc,
  similarTitle,
  complementartyRecc,
  complementartyTitle,
}: {
  similarRecc: Product[];
  complementartyRecc: Product[];
  similarTitle?: string;
  complementartyTitle?: string;
}) {
  const [activeTab, setActiveTab] = useState("recommended");
  const [activeCategories, setActiveCategories] = useState({
    recommended: "All",
    "complete-look": "All",
  });
  // const [parent] = useAutoAnimate();
  const [tabButtonsRef] = useAutoAnimate();

  const tabs = [
    {
      id: "recommended",
      label: similarTitle,
      products: similarRecc.length > 0 ? similarRecc : [],
      categories: [
        "All",
        ...new Set(similarRecc.map((product) => product.category)),
      ],
    },
    {
      id: "complete-look",
      label: complementartyTitle,
      products: complementartyRecc.length > 0 ? complementartyRecc : [],
      categories: [
        "All",
        ...new Set(complementartyRecc.map((product) => product.category)),
      ],
    },
  ];

  const activeTabData = tabs.find((tab) => tab.id === activeTab) || tabs[0];

  const filteredProducts = useMemo(() => {
    const activeCategory = activeCategories[activeTab];
    return activeCategory === "All"
      ? activeTabData.products
      : activeTabData.products.filter(
          (product) => product.category === activeCategory,
        );
  }, [activeTabData, activeCategories, activeTab]);

  const handleCategoryChange = (category: string) => {
    setActiveCategories((prev) => ({
      ...prev,
      [activeTab]: category,
    }));
  };

  return (
    <TrackWrapper
      event={{
        name: "cart-block",
        properties: {
          category: "cart-block",
        },
      }}
    >
      <div className="hp-mx-auto hp-px-0 hp-py-4">
        <div className="mb-2.5 flex w-full" ref={tabButtonsRef}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative z-10 flex-1 py-3 text-center text-xs uppercase lg:text-sm ${activeTab === tab.id ? "text-white" : "border-y border-y-[#181818] text-[#181818] hover:bg-secondary"}`}
            >
              {tab.label}
            </button>
          ))}
          <div
            className="absolute left-0 top-0 h-full bg-[#181818] transition-all duration-300 ease-in-out"
            style={{
              width: `${100 / tabs.length}%`,
              transform: `translateX(${tabs.findIndex((tab) => tab.id === activeTab) * 100}%)`,
            }}
          />
        </div>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={activeTab === tab.id ? "block" : "hidden"}
          >
            <div className="swimlane2 no-scrollbar flex flex-wrap gap-x-2 space-x-0 pl-2 lg:pb-0">
              {tab.categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`rounded-full px-2.5 py-0.5 text-xs capitalize ${
                    activeCategories[tab.id] === category
                      ? "bg-[#3300C3] text-white"
                      : "hover:bg-secondary/80 border border-[#181818] bg-transparent text-[#181818]"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
            <div className="no-scrollbar mt-2.5 flex w-full overflow-x-scroll pl-0 lg:pb-0">
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product, i) => (
                  <ProductCardSmall key={i} product={product} />
                ))
              ) : (
                <h5 className="flex min-h-[100px] w-full place-items-center justify-center text-sm font-semibold uppercase text-[#A7A7A7]">
                  No products found
                </h5>
              )}
            </div>
          </div>
        ))}
      </div>
    </TrackWrapper>
  );
}
